// The shareable view, in the query string.
//
// `?load=` already says which capture to fetch; these say what to do with it,
// so a link reproduces the picture the sender was looking at rather than the
// recipient's own saved options.
//
// Written only alongside `load`, because a link to a view of a file the
// recipient cannot fetch is not worth sharing, and only for values that differ
// from the defaults, so an ordinary link stays short and a long one is a real
// description of a specific view. Panel chrome is deliberately absent: which
// sections are collapsed and the export width are how someone drives the app,
// not what they are looking at.

import type { PlacedSignal } from './overlays/signal.ts'
import { isPaletteName } from './render/palette.ts'
import { defaultSettings, type Settings } from './state.ts'
import { isValidTimeZone } from './tz.ts'
import { parseDuration, parseFrequency, parseTimeInput, type TimeInput } from './units.ts'

export const LOAD_PARAM = 'load'

/** The window on screen, as frequency and time rather than clip coordinates. */
export interface ViewWindow {
  lowHz: number
  highHz: number
  startMs: number
  endMs: number
}

export interface ViewState {
  /** Only the settings the link actually carried; merge over the saved ones. */
  settings: Partial<Settings>
  /** Displayed frequency window, or null for the whole capture. */
  freq: { lowHz: number; highHz: number } | null
  /** Time window, or null for the whole capture. */
  time: { startMs: number; endMs: number } | null
  run: number
  /** The pinned centers, in list order. */
  pins: PlacedSignal[]
}

// ------------------------------------------------------------------ codecs

const finite = (text: string): number | undefined => {
  const n = Number(text)
  return Number.isFinite(n) ? n : undefined
}

const attempt = <T>(parse: (text: string) => T, text: string): T | undefined => {
  try {
    return parse(text)
  } catch {
    return undefined
  }
}

/** Hz compactly where it divides evenly, exact where it does not. */
function encodeHz(hz: number): string {
  if (hz === 0) return '0'
  if (hz % 1e6 === 0) return `${hz / 1e6}M`
  if (hz % 1e3 === 0) return `${hz / 1e3}k`
  return String(Math.round(hz))
}

function encodeSeconds(sec: number): string {
  if (sec % 3600 === 0) return `${sec / 3600}h`
  if (sec % 60 === 0) return `${sec / 60}m`
  return `${sec}s`
}

/** Epoch milliseconds as seconds, with a fraction only where one is needed. */
function encodeMs(ms: number): string {
  const sec = ms / 1000
  return Number.isInteger(sec) ? String(sec) : sec.toFixed(3).replace(/\.?0+$/, '')
}

function encodeTimeInput(value: TimeInput): string {
  if (value.kind === 'epoch') return encodeMs(value.ms)
  const n = value.naive
  const pad = (x: number) => String(x).padStart(2, '0')
  return `${n.year}-${pad(n.month)}-${pad(n.day)} ${pad(n.hour)}:${pad(n.minute)}:${pad(n.second)}`
}

type Key = keyof Settings

interface Param {
  key: Key
  name: string
  /** null to leave the parameter out entirely. */
  encode: (value: Settings[Key]) => string | null
  /** undefined to ignore a value the link should not have carried. */
  decode: (text: string) => Settings[Key] | undefined
}

/**
 * One entry per setting that changes the picture.
 *
 * Every nullable setting defaults to null, which is what lets "encodes to
 * nothing" and "is the default" be the same test below. A nullable setting
 * with a non-null default would need an explicit spelling for null.
 */
const PARAMS: Param[] = [
  { key: 'palette', name: 'palette', encode: (v) => String(v), decode: (t) => (isPaletteName(t) ? t : undefined) },
  { key: 'dbMin', name: 'dbmin', encode: (v) => (v === null ? null : String(v)), decode: finite },
  { key: 'dbMax', name: 'dbmax', encode: (v) => (v === null ? null : String(v)), decode: finite },
  { key: 'lowHz', name: 'low', encode: (v) => (v === null ? null : encodeHz(v as number)), decode: (t) => attempt(parseFrequency, t) },
  { key: 'highHz', name: 'high', encode: (v) => (v === null ? null : encodeHz(v as number)), decode: (t) => attempt(parseFrequency, t) },
  { key: 'offsetHz', name: 'offset', encode: (v) => encodeHz(v as number), decode: (t) => attempt(parseFrequency, t) },
  { key: 'begin', name: 'begin', encode: (v) => (v === null ? null : encodeTimeInput(v as TimeInput)), decode: (t) => attempt(parseTimeInput, t) },
  { key: 'end', name: 'end', encode: (v) => (v === null ? null : encodeTimeInput(v as TimeInput)), decode: (t) => attempt(parseTimeInput, t) },
  { key: 'headSec', name: 'head', encode: (v) => (v === null ? null : encodeSeconds(v as number)), decode: (t) => attempt(parseDuration, t) },
  { key: 'tailSec', name: 'tail', encode: (v) => (v === null ? null : encodeSeconds(v as number)), decode: (t) => attempt(parseDuration, t) },
  { key: 'compress', name: 'compress', encode: (v) => String(v), decode: (t) => finite(t) },
  { key: 'ytickSec', name: 'ytick', encode: (v) => (v === null ? null : encodeSeconds(v as number)), decode: (t) => attempt(parseDuration, t) },
  {
    key: 'timeDisplay',
    name: 'times',
    encode: (v) => String(v),
    decode: (t) => (t === 'utc' || t === 'local' ? t : undefined),
  },
  {
    key: 'captureZoneOverride',
    name: 'zone',
    encode: (v) => (v === null ? null : String(v)),
    decode: (t) => (isValidTimeZone(t) ? t : undefined),
  },
  {
    key: 'visibleSchemes',
    name: 'overlays',
    encode: (v) => ((v as string[]).length > 0 ? (v as string[]).join(',') : ''),
    decode: (t) => t.split(',').filter((id) => id !== ''),
  },
  {
    key: 'signalCheckOn',
    name: 'check',
    encode: (v) => (v ? '1' : '0'),
    decode: (t) => t === '1' || t === 'true',
  },
  { key: 'signalBwHz', name: 'bw', encode: (v) => String(v), decode: finite },
  { key: 'signalSf', name: 'sf', encode: (v) => String(v), decode: finite },
  { key: 'signalCr', name: 'cr', encode: (v) => String(v), decode: finite },
  { key: 'payloadBytes', name: 'bytes', encode: (v) => String(v), decode: finite },
  {
    key: 'thresholdMode',
    name: 'occ',
    encode: (v) => String(v),
    decode: (t) => (t === 'relative' || t === 'absolute' ? t : undefined),
  },
  { key: 'thresholdMarginDb', name: 'margin', encode: (v) => String(v), decode: finite },
  { key: 'thresholdAbsDb', name: 'level', encode: (v) => String(v), decode: finite },
  { key: 'fitLossBudgetDb', name: 'budget', encode: (v) => String(v), decode: finite },
  {
    key: 'filterOn',
    name: 'filter',
    encode: (v) => (v ? '1' : '0'),
    decode: (t) => t === '1' || t === 'true',
  },
  { key: 'filterCenterHz', name: 'fc', encode: (v) => (v === null ? null : encodeHz(v as number)), decode: (t) => attempt(parseFrequency, t) },
  { key: 'filterBwHz', name: 'fbw', encode: (v) => (v === null ? null : encodeHz(v as number)), decode: (t) => attempt(parseFrequency, t) },
  { key: 'filterOrder', name: 'skirt', encode: (v) => String(v), decode: finite },
  { key: 'filterRejectionDb', name: 'rej', encode: (v) => String(v), decode: finite },
]

// -------------------------------------------------------------- read, write

export function loadSource(search: string): string | null {
  return new URLSearchParams(search).get(LOAD_PARAM)
}

function pair(text: string | null): [number, number] | null {
  if (!text) return null
  const parts = text.split(',')
  if (parts.length !== 2) return null
  const a = Number(parts[0])
  const b = Number(parts[1])
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return a <= b ? [a, b] : [b, a]
}

export function readViewState(search: string): ViewState {
  const params = new URLSearchParams(search)
  const settings: Record<string, unknown> = {}
  for (const param of PARAMS) {
    const text = params.get(param.name)
    if (text === null) continue
    const value = param.decode(text)
    // A malformed value is dropped rather than failing the link: a shared URL
    // is typed and edited by people, and one bad field should not blank a view.
    if (value !== undefined) settings[param.key] = value
  }

  const freq = pair(params.get('f'))
  const time = pair(params.get('t'))
  const run = Number(params.get('run'))
  // One `signal` per pin rather than one packed field: repeating a parameter
  // is the form a query string already has for a list, and a bad one drops on
  // its own instead of taking the rest of the pins with it.
  const pins: PlacedSignal[] = []
  for (const text of params.getAll('signal')) {
    const parts = text.split('@')
    const hz = parts[0] ? attempt(parseFrequency, parts[0]) : undefined
    const at = parts[1] ? Number(parts[1]) : undefined
    if (hz !== undefined && at !== undefined && Number.isFinite(at)) pins.push({ centerHz: hz, startMs: at * 1000 })
  }

  return {
    settings: settings as Partial<Settings>,
    freq: freq ? { lowHz: freq[0], highHz: freq[1] } : null,
    time: time ? { startMs: time[0] * 1000, endMs: time[1] * 1000 } : null,
    run: Number.isInteger(run) && run > 0 ? run : 0,
    pins,
  }
}

/**
 * The query string for a view: `load` first, then whatever differs from a
 * fresh session. "Differs" is measured on the encoded text, so one rule covers
 * every setting and there is no second list of defaults to keep in step.
 */
export function viewQuery(source: string, state: ViewState): string {
  const params = new URLSearchParams()
  params.set(LOAD_PARAM, source)

  const defaults = defaultSettings()
  for (const param of PARAMS) {
    const value = state.settings[param.key]
    if (value === undefined) continue
    const now = param.encode(value)
    if (now === null) continue
    if (now === param.encode(defaults[param.key])) continue
    params.set(param.name, now)
  }

  if (state.run > 0) params.set('run', String(state.run))
  if (state.freq) params.set('f', `${Math.round(state.freq.lowHz)},${Math.round(state.freq.highHz)}`)
  if (state.time) params.set('t', `${encodeMs(state.time.startMs)},${encodeMs(state.time.endMs)}`)
  for (const pin of state.pins) params.append('signal', `${Math.round(pin.centerHz)}@${encodeMs(pin.startMs)}`)

  // A link is meant to be read and hand-edited, and RFC 3986 allows all of
  // these raw in a query. URLSearchParams escapes them anyway and reads them
  // back either way, so they are put back for legibility. Spaces stay as '+'.
  const unescaped: Record<string, string> = { '%2F': '/', '%2C': ',', '%3A': ':', '%40': '@' }
  return `?${params.toString().replace(/%2F|%2C|%3A|%40/g, (m) => unescaped[m]!)}`
}
